import { Helmet } from "react-helmet-async";
import { SITE_URL, SITE_NAME, OG_IMAGE, PHONE, EMAIL, REGISTERED_ADDRESS as ADDRESS, FOUNDER, FOUNDER_IMG, LEGAL_NAME, SIRET } from "@/data/site";
import { PAGE_METADATA } from "@/data/seo";
import { useHeadMetadata } from "./HeadMetadata";

export function SEO({ title, description, path = "/", image = OG_IMAGE, type = "website", noindex = false }) {
  title = PAGE_METADATA[path]?.title || title;
  description = PAGE_METADATA[path]?.description || description;
  image = `${SITE_URL}/images/ozc-immo-partage.jpg`;
  const url = `${SITE_URL}${path}`;
  const named = (name, content) => ({ tag: "meta", key: name, attrs: { name, content } });
  const property = (name, content) => ({ tag: "meta", key: name, attrs: { property: name, content } });
  useHeadMetadata([
    { tag: "title", key: "title", text: title }, named("description", description),
    { tag: "link", key: "canonical", attrs: { rel: "canonical", href: url } },
    named("robots", noindex ? "noindex, follow" : "index, follow, max-image-preview:large, max-snippet:-1"),
    named("author", `${SITE_NAME} — ${FOUNDER}`),
    { tag: "meta", key: "language", attrs: { "http-equiv": "content-language", content: "fr-FR" } },
    ...["fr-FR", "x-default"].map(hreflang => ({ tag: "link", key: `alternate-${hreflang}`, attrs: { rel: "alternate", hreflang, href: url } })),
    property("og:title", title), property("og:description", description), property("og:url", url), property("og:type", type),
    property("og:site_name", SITE_NAME), property("og:image", image),
    property("og:image:alt", "OZC IMMO — Conciergerie et sous-location professionnelle"),
    property("og:image:width", "1200"), property("og:image:height", "630"), property("og:locale", "fr_FR"),
    named("twitter:card", "summary_large_image"), named("twitter:title", title), named("twitter:description", description), named("twitter:image", image),
  ]);
  return null;
}

const JsonLd = ({ data }) => (
  <Helmet>
    <script type="application/ld+json">{JSON.stringify(data)}</script>
  </Helmet>
);

export function OrganizationSchema() {
  const data = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": ["Organization", "ProfessionalService"],
        "@id": `${SITE_URL}/#organization`,
        name: SITE_NAME,
        legalName: LEGAL_NAME,
        identifier: { "@type": "PropertyValue", propertyID: "SIRET", value: SIRET },
        url: `${SITE_URL}/`,
        logo: `${SITE_URL}/favicon.svg`,
        image: `${SITE_URL}${OG_IMAGE}`,
        description:
          "Conciergerie et sous-location professionnelle en Alsace, sur la Côte d’Azur et à Marrakech. Accompagnement individuel à distance en France pour les propriétaires et futurs professionnels.",
        telephone: PHONE,
        email: EMAIL,
        address: {
          "@type": "PostalAddress",
          streetAddress: ADDRESS.street,
          addressLocality: ADDRESS.city,
          postalCode: ADDRESS.postalCode,
          addressRegion: "Grand Est",
          addressCountry: "FR",
        },
        founder: { "@id": `${SITE_URL}/#founder` },
        areaServed: [
          { "@type": "AdministrativeArea", name: "Alsace" },
          { "@type": "AdministrativeArea", name: "Côte d'Azur" },
          { "@type": "City", name: "Marrakech" },
        ],
        knowsAbout: [
          "Sous-location professionnelle",
          "Conciergerie immobilière",
          "Gestion de locations courte durée",
          "Gestion de villas et appartements premium",
          "Accompagnement en conciergerie et sous-location professionnelle",
        ],
      },
      {
        "@type": "Person",
        "@id": `${SITE_URL}/#founder`,
        name: FOUNDER,
        jobTitle: "Fondateur & Gérant",
        image: `${SITE_URL}${FOUNDER_IMG}`,
        worksFor: { "@id": `${SITE_URL}/#organization` },
        url: `${SITE_URL}/a-propos`,
      },
      {
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`,
        url: SITE_URL,
        name: SITE_NAME,
        inLanguage: "fr-FR",
        publisher: { "@id": `${SITE_URL}/#organization` },
      },
    ],
  };
  return <JsonLd data={data} />;
}

export function BreadcrumbSchema({ items }) {
  return (
    <JsonLd
      data={{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items.map((item, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: item.name,
          item: `${SITE_URL}${item.path}`,
        })),
      }}
    />
  );
}

export function FAQSchema({ faqs }) {
  return (
    <JsonLd
      data={{
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }}
    />
  );
}

export function ServiceSchema({ name, description, areaServed, path, alternateName, serviceType = "Property management" }) {
  return (
    <JsonLd
      data={{
        "@context": "https://schema.org",
        "@type": "Service",
        "@id": `${SITE_URL}${path}#service`,
        name,
        alternateName,
        description,
        serviceType,
        url: `${SITE_URL}${path}`,
        provider: { "@id": `${SITE_URL}/#organization` },
        areaServed: areaServed.map((a) => ({ "@type": "Place", name: a })),
      }}
    />
  );
}

export function ArticleSchema({ article }) {
  return (
    <JsonLd
      data={{
        "@context": "https://schema.org",
        "@type": "Article",
        headline: article.title,
        description: article.metaDescription,
        image: `${SITE_URL}${article.imageUrl}`,
        datePublished: article.date,
        dateModified: article.updatedAt || article.date,
        inLanguage: "fr-FR",
        mainEntityOfPage: `${SITE_URL}/conseils/${article.slug}`,
        author: { "@id": `${SITE_URL}/#founder` },
        publisher: { "@id": `${SITE_URL}/#organization` },
      }}
    />
  );
}
